import { z } from "zod";
import { zTipoItem } from "./dominio.js";
import { zRelatorio } from "./importar-contrato.js";
/**
 * O contrato da IA — RN-61 a RN-63.
 *
 * **A chave nunca volta.** `zEstadoDaIa` carrega se existe uma, os quatro
 * últimos caracteres dela e de quem ela é; a chave em si só viaja no sentido
 * navegador → servidor, uma vez, na hora de cadastrar. O invariante 15 é sobre
 * segredo gerado aqui, e vale igual para segredo que chega de fora: uma rota
 * que devolvesse a chave a colocaria em cache de navegador, em log de proxy e
 * em qualquer relatório de erro que serialize resposta.
 *
 * **A geração de itens não devolve itens gravados.** Ela devolve o conteúdo
 * cru mais o **mesmo relatório** da tela de importar: quem grava continua sendo
 * `importacao.confirmar`, depois de um "confirmar" humano (RN-21). Uma rota que
 * gerasse e gravasse de uma vez tiraria do caminho a única etapa que existe
 * para pegar o item errado antes de ele virar estudo.
 */
export const zEstadoDaIa = z.object({
    /** `true` quando esta conta cadastrou a própria chave. */
    temChaveDaConta: z.boolean(),
    /** Os quatro últimos caracteres, para reconhecer qual é sem revelá-la. */
    finalDaChave: z.string().nullable(),
    /** `true` quando o servidor tem algum provedor configurado (ADR 042). */
    temChaveDoServidor: z.boolean(),
    usadasHoje: z.number().int(),
    cotaDiaria: z.number().int(),
    /** A cadeia de provedores do servidor, em ordem ("groq · gemini"). */
    modelo: z.string(),
});
export const zChaveDaIa = z.object({
    /**
     * Espaço nas pontas é aparado: chave colada de um painel web quase sempre vem
     * com um deles, e uma chave com espaço falha com "credencial inválida" — o
     * diagnóstico que mais custa tempo por ser o menos provável de se desconfiar.
     */
    chave: z.string().trim().min(8, "Isso é curto demais para ser uma chave de API").max(400),
});
export const zPedidoDeItensIa = z.object({
    assunto: z.string().trim().max(4000),
    /** O id da categoria escolhida, ou `null` para o modelo derivar o caminho. */
    categoriaId: z.string().nullable().default(null),
    quantidade: z.number().int().min(1).max(50),
    tipos: z.array(zTipoItem).default([]),
    dificuldade: z.string().trim().max(40).nullable().default(null),
    lote: z.string().trim().min(1).max(120),
    /**
     * Fontes com texto (provas, materiais) que o pedido leva de referência, para
     * as questões seguirem o estilo e o nível delas (ADR 043).
     */
    fontes: z.array(z.string()).max(20).default([]),
});
export const zItensGerados = z.object({
    /** O JSON como o modelo devolveu, recortado da prosa. Vai para o campo da tela. */
    conteudo: z.string(),
    /** O relatório da tela de importar, do mesmo validador (RN-21 a RN-25). */
    relatorio: zRelatorio,
    origemDaChave: z.enum(["conta", "servidor"]),
});
/** Qual bloco da prova extrair — ADR 045. A tela pede um de cada vez, em ordem. */
export const zPedidoDeExtracao = z.object({
    bloco: z.number().int().min(0).max(199),
    /**
     * Os assuntos que os blocos anteriores já usaram. Sem eles cada bloco
     * batiza o mesmo tema de um jeito ("Lei Áurea", "Abolição da escravidão"),
     * e a árvore da trilha ganha uma categoria irmã para cada grafia.
     */
    assuntos: z.array(z.string().trim().min(1).max(80)).max(60).default([]),
});
/**
 * Um bloco de prova já no formato de importação. A tela junta os blocos num
 * envelope e manda à conferência de sempre (`importacao.validar`): nada aqui
 * foi validado nem gravado.
 */
export const zBlocoExtraido = z.object({
    totalBlocos: z.number().int().min(1),
    /** O título da fonte, para o nome do lote e o campo `fonte` do envelope. */
    titulo: z.string(),
    textosBase: z.record(z.string(), z.string()),
    itens: z.array(z.record(z.string(), z.unknown())),
    /** Quantos itens vieram com gabarito resolvido pelo modelo. */
    semGabarito: z.number().int().min(0),
});
export const zPerguntaDaIa = z.object({
    /** Vazio significa "explique este item" — a pergunta padrão de quem travou. */
    pergunta: z.string().trim().max(2000).default(""),
});
export const zRespostaDaIa = z.object({
    /**
     * Markdown, não HTML.
     *
     * A tela renderiza pelo caminho de `ConteudoRico`, que já sabe recusar o que
     * não está no vocabulário fechado (invariante 17). HTML vindo de um modelo
     * seria conteúdo de terceiro entrando na página — e este é o único lugar do
     * app onde o texto não veio nem do usuário nem do importador.
     */
    markdown: z.string(),
    origemDaChave: z.enum(["conta", "servidor"]),
});
//# sourceMappingURL=ia.js.map